import React, { useState, useEffect, useCallback } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import HelpTrigger from '../../../../help/components/HelpTrigger';
import { useAnalyticsViews, type AnalyticsView } from '../../../../hooks/useAnalyticsViews';
import WizardStepIndicator from '../../wizard/WizardStepIndicator';
import WizardNavigation from '../../wizard/WizardNavigation';
import ReportStep1BasicInfo from './ReportStep1BasicInfo';
import ReportStep2Filters from './ReportStep2Filters';
import ReportStep3Charts from './ReportStep3Charts';
import ReportStep4Settings from './ReportStep4Settings';
import ReportStep5Preview from './ReportStep5Preview';

interface OutreachReportWizardProps {
  itemId: string;
  itemName?: string;
  existingView: AnalyticsView | null;
  onClose: (reportSaved?: boolean) => void;
  initialDefaultPublic?: boolean;
}

const STEPS = [
  { number: 1, label: 'Basic Info' },
  { number: 2, label: 'Filters' },
  { number: 3, label: 'Charts' },
  { number: 4, label: 'Settings' },
  { number: 5, label: 'Preview' }
];

export interface ReportFormState {
  name: string;
  description: string;
  isDefaultPublic: boolean;
  filtersJson: Record<string, any>;
  sectionsJson: {
    overview_stats: boolean;
    summary: boolean;
    charts: boolean;
    responses_table: boolean;
    response_columns: string[];
    /** Viewer downloads on shared report pages. Absent = allowed (every
     *  pre-existing report keeps its exports); false = buttons hidden and
     *  the public export endpoint returns 403. Staff exports unaffected. */
    allow_export?: boolean;
  };
  summaryHtml: string;
  expiresInDays: number | null;
}

const DEFAULT_STATE: ReportFormState = {
  name: '',
  description: '',
  isDefaultPublic: false,
  filtersJson: {},
  sectionsJson: {
    overview_stats: true,
    summary: false,
    charts: true,
    responses_table: false,
    response_columns: [],
    allow_export: true
  },
  summaryHtml: '',
  expiresInDays: null
};

const OutreachReportWizard: React.FC<OutreachReportWizardProps> = ({ itemId, itemName, existingView, onClose, initialDefaultPublic }) => {
  const { createView, updateView } = useAnalyticsViews(itemId);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [viewId, setViewId] = useState<string | null>(existingView?.id || null);

  const [formState, setFormState] = useState<ReportFormState>(() => {
    if (existingView) {
      return {
        name: existingView.name,
        description: existingView.description || '',
        isDefaultPublic: existingView.is_default_public,
        filtersJson: existingView.filters_json || {},
        sectionsJson: existingView.sections_json as ReportFormState['sectionsJson'] || DEFAULT_STATE.sectionsJson,
        summaryHtml: existingView.summary_html || '',
        expiresInDays: null
      };
    }
    return { ...DEFAULT_STATE, isDefaultPublic: initialDefaultPublic || false };
  });

  const updateForm = (updates: Partial<ReportFormState>) => {
    setFormState(prev => ({ ...prev, ...updates }));
  };

  const isEditing = !!existingView;

  const isStepValid = useCallback(() => {
    switch (currentStep) {
      case 1: return formState.name.trim().length > 0;
      default: return true;
    }
  }, [currentStep, formState.name]);

  const handleSave = async () => {
    if (!formState.name.trim()) return;
    setSaving(true);
    try {
      if (viewId) {
        await updateView({
          viewId,
          name: formState.name,
          description: formState.description || undefined,
          filtersJson: formState.filtersJson,
          sectionsJson: formState.sectionsJson,
          summaryHtml: formState.summaryHtml || undefined,
          isDefaultPublic: formState.isDefaultPublic,
          expiresInDays: formState.expiresInDays || undefined
        });
      } else {
        const created = await createView({
          name: formState.name,
          description: formState.description || undefined,
          filtersJson: formState.filtersJson,
          sectionsJson: formState.sectionsJson,
          summaryHtml: formState.summaryHtml || undefined,
          isDefaultPublic: formState.isDefaultPublic,
          expiresInDays: formState.expiresInDays || undefined
        });
        setViewId(created.id);
      }
    } catch {
      // Errors handled by hook
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (currentStep === 2 && !viewId && formState.name.trim()) {
      handleSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleNext = async () => {
    if (!isStepValid()) return;

    if (currentStep === 1 && !viewId && formState.name.trim()) {
      setSaving(true);
      try {
        const created = await createView({
          name: formState.name,
          description: formState.description || undefined,
          isDefaultPublic: formState.isDefaultPublic
        });
        setViewId(created.id);
      } catch {
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    setCompletedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFinish = async () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    await handleSave();
    onClose(true);
  };

  const canNavigateToStep = (step: number) => {
    if (step === 1) return true;
    return !!viewId || completedSteps.has(step) || step <= currentStep;
  };

  const handleStepClick = (step: number) => {
    if (canNavigateToStep(step)) {
      setCurrentStep(step);
    }
  };

  const stepIndicatorSteps = STEPS.map(s => ({
    ...s,
    completed: completedSteps.has(s.number)
  }));

  // Steps 3 and 5 need a saved report. If the initial save failed they used to render
  // nothing at all, leaving a blank panel with working navigation and no explanation.
  const notSavedYet = (detail: string) => (
    <div className="max-w-xl mx-auto card p-6 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-medium text-text-primary dark:text-text-dark-primary">
          This report hasn&apos;t been saved yet
        </p>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
          {detail} Go back to Basic Info, check the name, and continue again — if it keeps
          failing, the save is being rejected.
        </p>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ReportStep1BasicInfo itemId={itemId} formState={formState} onChange={updateForm} />;
      case 2:
        return <ReportStep2Filters itemId={itemId} formState={formState} onChange={updateForm} />;
      case 3:
        return viewId
          ? <ReportStep3Charts itemId={itemId} viewId={viewId} formState={formState} />
          : notSavedYet('Charts are added to a saved report.');
      case 4:
        return <ReportStep4Settings itemId={itemId} formState={formState} onChange={updateForm} />;
      case 5:
        return viewId ? (
          <ReportStep5Preview
            itemId={itemId}
            viewId={viewId}
            shareToken={existingView?.share_token || ''}
            formState={formState}
          />
        ) : notSavedYet('The preview renders the saved report.');
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={true}
      title={isEditing ? 'Edit report' : 'Create report'}
      description={itemName || undefined}
      titleAccessory={<HelpTrigger contentKey="outreach-report-wizard" size="sm" />}
      onClose={() => onClose()}
      size="xl"
      className="sm:h-[90vh] sm:max-w-5xl"
      /* The wizard owns its own step indicator and navigation, which have to
         span the full panel — the dialog's body padding would inset them. */
      bodyClassName="flex min-h-0 flex-1 flex-col"
    >
          {/* Step Indicator */}
          <div className="flex-shrink-0">
            <WizardStepIndicator
              steps={stepIndicatorSteps}
              currentStep={currentStep}
              onStepClick={handleStepClick}
              canNavigateToStep={canNavigateToStep}
            />
          </div>

          {/* Step Content - p-6 on all sides, same as OutreachCreationWizard */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex-shrink-0">
            <WizardNavigation
              currentStep={currentStep}
              totalSteps={STEPS.length}
              canGoBack={currentStep > 1}
              canGoNext={currentStep < STEPS.length}
              onBack={handleBack}
              onNext={handleNext}
              onSaveDraft={handleSave}
              onLaunch={handleFinish}
              saving={saving}
              isStepValid={isStepValid()}
              blockedReason={currentStep === 1 && !formState.name.trim() ? 'Name the report to continue' : undefined}
              // Reports have no draft state — this button just saves.
              saveDraftLabel="Save"
              savingLabel="Saving..."
              launchLabel="Save & Finish"
              launchingLabel="Saving..."
              launchIcon={<Check className="w-4 h-4 mr-2" />}
            />
          </div>
    </Modal>
  );
};

export default OutreachReportWizard;
